import { Box, Divider, Grid, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import TaxSummaryHover from '../../../common/Tax/TaxSummaryHover';
import { computeLabourPartsFromServices } from '../../../utility/helpers/computeLabourPartsFromServices';
import { formatDiscountAmountDisplay } from '../../../utility/helpers/discountCalculation';
import { NumericEngine } from '../../../utility/helpers/numericEngine';
import { DOCUMENT_TAX_TYPE, formatTaxPercent, isNewTaxVersion, summarizeTaxFromRecord } from '../../../utility/helpers/taxCalculation';
import { useTaxDecimalPrecision } from '../../../utility/hooks/useTaxDecimalPrecision';

const formatSummaryCurrency = (n) => (Number(n) > 0 ? NumericEngine.format(n, { formatCurrency: true, trailingZeros: true }) : '$0.00');

/**
 * Totals block for the work order detail page.
 *
 * Identical to InvoicePaymenetDetails minus the invoice-only parts — payments,
 * balance chips and refunds — which a work order never has (spec §5).
 * Tax rows read `workOrderAuthorizedRate`, the work order's own rate key (spec §8.3).
 */
const WorkOrderPaymentDetails = ({ workOrderData = {} }) => {
    const taxPrecision = useTaxDecimalPrecision();

    const { labourTotal, partsTotal, packagesTotal } = useMemo(() => {
        // The record carries its own component totals (as strings); fall back to deriving them
        // from the services when an older record does not.
        if (workOrderData?.laborTotal != null || workOrderData?.partTotal != null || workOrderData?.servicePackageTotal != null) {
            return {
                labourTotal: Number(workOrderData?.laborTotal) || 0,
                partsTotal: Number(workOrderData?.partTotal) || 0,
                packagesTotal: Number(workOrderData?.servicePackageTotal) || 0,
            };
        }
        if (Array.isArray(workOrderData?.services) && workOrderData.services.length > 0) {
            return computeLabourPartsFromServices(workOrderData.services);
        }
        return { labourTotal: 0, partsTotal: 0, packagesTotal: 0 };
    }, [workOrderData?.laborTotal, workOrderData?.partTotal, workOrderData?.servicePackageTotal, workOrderData?.services]);

    const appliedTaxLines = useMemo(() => {
        if (!isNewTaxVersion(workOrderData)) return [];
        return summarizeTaxFromRecord(workOrderData, DOCUMENT_TAX_TYPE.WORK_ORDER).filter((row) => row.applyTax && Number(row.taxAmount) > 0);
    }, [workOrderData]);

    const totalTax = appliedTaxLines?.length > 0 ? appliedTaxLines.reduce((acc, r) => NumericEngine.add(acc, String(r.taxAmount || 0)), '0') : 0;

    return (
        <Grid container justifyContent={'space-between'} padding={'0 16px 24px 24px'}>
            <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={1}>
                <Typography>Labour</Typography>
                <Typography>{formatSummaryCurrency(labourTotal)}</Typography>
            </Stack>
            <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={1}>
                <Typography>Parts</Typography>
                <Typography>{formatSummaryCurrency(partsTotal)}</Typography>
            </Stack>
            <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={1}>
                <Typography>Service Packages</Typography>
                <Typography>{formatSummaryCurrency(packagesTotal)}</Typography>
            </Stack>

            <Divider sx={{ backgroundColor: '#0000001F', margin: '8px 0', width: '100%' }} />

            <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={1}>
                <Typography>Subtotal</Typography>
                <Typography>
                    {workOrderData?.subTotal ? NumericEngine.format(workOrderData.subTotal, { formatCurrency: true, trailingZeros: true }) : '$0.00'}
                </Typography>
            </Stack>

            {workOrderData?.discount > 0 && (
                <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={1}>
                    <Box>
                        {workOrderData?.membershipProgram?.programName
                            ? `Discount - ${workOrderData?.membershipProgram?.programName} (${workOrderData?.membershipProgram?.discount}%)`
                            : 'Discount'}
                    </Box>
                    <Typography>
                        {formatDiscountAmountDisplay({
                            discount: workOrderData?.discount,
                            discountPercentage: workOrderData?.discountPercentage,
                            discountSource: workOrderData?.discountSource,
                        })}
                    </Typography>
                </Stack>
            )}

            {workOrderData?.totalFeesAmount > 0 && (
                <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={1}>
                    <Typography>Fees</Typography>
                    <Typography>{NumericEngine.format(workOrderData.totalFeesAmount, { formatCurrency: true, trailingZeros: true })}</Typography>
                </Stack>
            )}

            {isNewTaxVersion(workOrderData) ? (
                appliedTaxLines.map((row) => (
                    <Stack
                        key={row.taxId || row.name}
                        direction={'row'}
                        width={'100%'}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                        gap={2}
                        mb={'8px'}
                    >
                        <TaxSummaryHover summary={row}>
                            <Typography>
                                {row.name} ({formatTaxPercent(row.rate, taxPrecision)}%)
                            </Typography>
                        </TaxSummaryHover>
                        <TaxSummaryHover summary={row}>
                            <Typography>{formatSummaryCurrency(row.taxAmount)}</Typography>
                        </TaxSummaryHover>
                    </Stack>
                ))
            ) : (
                <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={0}>
                    <Typography>Tax</Typography>
                    <Typography>{formatSummaryCurrency(workOrderData?.taxAmount ?? workOrderData?.tax)}</Typography>
                </Stack>
            )}

            {isNewTaxVersion(workOrderData) && appliedTaxLines?.length > 1 && (
                <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={'8px'}>
                    <Typography sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>Total Tax</Typography>
                    <Typography sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>{formatSummaryCurrency(totalTax)}</Typography>
                </Stack>
            )}

            <Divider sx={{ backgroundColor: '#0000001F', margin: '4px 0 12px 0', width: '100%' }} />

            <Grid item xs={6}>
                <Typography fontWeight={600} sx={{ fontSize: '18px' }}>
                    Total
                </Typography>
            </Grid>
            <Grid item xs={6} textAlign={'end'}>
                <Typography fontWeight={600} sx={{ fontSize: '18px' }}>
                    {workOrderData?.total ? NumericEngine.format(workOrderData.total, { formatCurrency: true, trailingZeros: true }) : '$0.00'}
                </Typography>
            </Grid>
        </Grid>
    );
};

export default WorkOrderPaymentDetails;
